import React from 'react';
import { SimulationConfig } from '@/types';
import { VisualStylesSection } from './VisualStylesSection';
import { ShapeSettingsSection } from './ShapeSettingsSection';
import { SphereDisplaySettings } from './SphereDisplaySettings';
import { TrailSettingsSection } from './TrailSettingsSection';
import { ImpactFXSection } from './ImpactFXSection';
import { LayerSortingSection } from './LayerSortingSection';
import { GlobalVisualsSection } from './GlobalVisualsSection';
import { GeneralDisplaySection } from './GeneralDisplaySection';
import { ParallaxSettingsSection } from './ParallaxSettingsSection';
import { TitleFooterSettingsSection } from './TitleFooterSettingsSection';
import { CollapsibleSection } from './CollapsibleSection';
import { 
  Circle, 
  Shapes, 
  Wind, 
  Zap, 
  Layers, 
  Sparkles,
  Monitor,
  Layout,
  Type
} from 'lucide-react';

interface VisualsTabProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  getLockInfo: (settingId: string) => { isLocked: boolean; min_plan?: string };
  onUpgradeClick: () => void;
}

export const VisualsTab: React.FC<VisualsTabProps> = ({ config, setConfig, getLockInfo, onUpgradeClick }) => {
  const isLocked = (id: string) => getLockInfo(id).isLocked;
  const plan = (id: string) => getLockInfo(id).min_plan as any;

  return (
    <div className="flex flex-col w-full gap-0">
      <CollapsibleSection 
        title="Sphere Studio" 
        icon={Circle} 
        darkMode={config.darkMode} 
        settingKey="sphereStudio"
        isLocked={isLocked('sphereDisplay') || isLocked('sphereStudio')}
        lockRequiredPlan={plan('sphereDisplay') || plan('sphereStudio')}
        onUpgradeClick={onUpgradeClick}
        defaultOpen
      >
          <div className="mt-4">
            <SphereDisplaySettings config={config} setConfig={setConfig} isLocked={isLocked('sphereDisplay')} />
          </div>
      </CollapsibleSection>

      <CollapsibleSection 
        title="Master Display" 
        icon={Monitor} 
        darkMode={config.darkMode} 
        settingKey="masterDisplayControls"
        isLocked={isLocked('masterDisplayControls') || isLocked('generalDisplay')}
        lockRequiredPlan={plan('masterDisplayControls') || plan('generalDisplay')}
        onUpgradeClick={onUpgradeClick}
      >
          <div className="mt-4">
            <GeneralDisplaySection config={config} setConfig={setConfig} isLocked={isLocked('masterDisplayControls')} getLockInfo={getLockInfo} />
          </div>
      </CollapsibleSection>

      <VisualStylesSection config={config} setConfig={setConfig} isLocked={isLocked('visualStyles')} getLockInfo={getLockInfo} />
      
      <CollapsibleSection 
        title="Geometry & World" 
        icon={Shapes} 
        darkMode={config.darkMode} 
        settingKey="geometryWorld"
        isLocked={isLocked('shapeSettings') || isLocked('geometry')}
        lockRequiredPlan={plan('shapeSettings') || plan('geometry')}
        onUpgradeClick={onUpgradeClick}
      >
          <div className="mt-4">
            <ShapeSettingsSection config={config} setConfig={setConfig} isLocked={isLocked('shapeSettings')} getLockInfo={getLockInfo} />
          </div>
      </CollapsibleSection>

      <CollapsibleSection 
        title="Trail Dynamics" 
        icon={Wind} 
        darkMode={config.darkMode} 
        settingKey="trailDynamics"
        isLocked={isLocked('trails')}
        lockRequiredPlan={plan('trails')}
        onUpgradeClick={onUpgradeClick}
      >
          <div className="mt-4">
            <TrailSettingsSection config={config} setConfig={setConfig} isLocked={isLocked('trails')} getLockInfo={getLockInfo} />
          </div>
      </CollapsibleSection>

      <CollapsibleSection 
        title="Impact Effects" 
        icon={Zap} 
        darkMode={config.darkMode} 
        settingKey="impactEffects"
        isLocked={isLocked('impactFX') || isLocked('blastFXColor')}
        lockRequiredPlan={plan('impactFX') || plan('blastFXColor')}
        onUpgradeClick={onUpgradeClick}
      >
          <div className="mt-4">
            <ImpactFXSection config={config} setConfig={setConfig} isLocked={isLocked('impactFX')} getLockInfo={getLockInfo} />
          </div>
      </CollapsibleSection>

      <CollapsibleSection 
        title="Parallax Engine" 
        icon={Layout} 
        darkMode={config.darkMode} 
        settingKey="parallaxEngine"
        isLocked={isLocked('parallax')}
        lockRequiredPlan={plan('parallax')}
        onUpgradeClick={onUpgradeClick}
      >
          <div className="mt-4">
            <ParallaxSettingsSection config={config} setConfig={setConfig} />
          </div>
      </CollapsibleSection>

      <CollapsibleSection 
        title="Title & Branding" 
        icon={Type} 
        darkMode={config.darkMode} 
        settingKey="titleBranding"
        isLocked={isLocked('titleFooter')}
        lockRequiredPlan={plan('titleFooter')}
        onUpgradeClick={onUpgradeClick}
      >
          <div className="mt-4">
            <TitleFooterSettingsSection config={config} setConfig={setConfig} />
          </div>
      </CollapsibleSection>

      <CollapsibleSection 
        title="Layer Sorting" 
        icon={Layers} 
        darkMode={config.darkMode} 
        settingKey="layerSorting"
        isLocked={isLocked('layersSorting')}
        lockRequiredPlan={plan('layersSorting')}
        onUpgradeClick={onUpgradeClick}
      >
          <div className="mt-4">
            <LayerSortingSection config={config} setConfig={setConfig} />
          </div>
      </CollapsibleSection>

      <GlobalVisualsSection 
        config={config} 
        setConfig={setConfig} 
        isLocked={isLocked('globalVisuals') || isLocked('rgbEngine')} 
        minPlan={plan('globalVisuals') || plan('rgbEngine')}
        onUpgradeClick={onUpgradeClick}
      />
    </div>
  );
};
